/* =========================================================
   MVHR / MEV Kit Builder
   Flow:
   - First submit captures lead + sends Zoho + builds quote on screen
   - Later changes only rebuild kit (no duplicate Zoho sends)
========================================================= */

const QUICK_QUOTE_PROXY = "/apps/quick-quote";
const CRM_SOURCE_TAG = "mvhr-mev-kitbuilder-preview";

let systemType = "mvhr";
let lastCalc = null;
let lastKitLines = [];
let calcRunId = 0;
let lastPlanAnalysis = null;
let lastPlanSuggestedOptions = null;
let leadCaptured = false;



const KIT_COMPONENT_HELP = {
  // MVHR units
  "443319": "MVHR unit. The main ventilation unit that extracts stale air and supplies fresh filtered air while recovering heat from the outgoing air.",
  "408167": "MVHR unit. A whole-house heat recovery system that balances supply and extract airflow.",
  "499883": "MVHR unit. A heat recovery ventilation unit that continuously brings in fresh air and removes stale air.",
  "NOTUS/HV/350": "MVHR unit. The central heat recovery ventilation unit for the property.",
  "443028": "MVHR unit. Provides whole-house balanced ventilation with heat recovery.",
  "499632": "MVHR unit. A larger MVHR unit for higher airflow requirements.",
  "408449": "MVHR unit. High-capacity MVHR unit for larger homes or higher airflow demand.",
  "499641": "MVHR unit. Large-capacity heat recovery ventilation unit.",
  "VL-250CZPVU-R-E": "MVHR unit. Mitsubishi Lossnay heat recovery ventilation unit.",
  "VL-350CZPVU-R-E": "MVHR unit. Mitsubishi Lossnay heat recovery ventilation unit for higher airflow.",
  "VL-500CZPVU-R-E": "MVHR unit. High-capacity Mitsubishi Lossnay heat recovery ventilation unit.",
  "VL-500CZPVU-L-E": "MVHR unit. High-capacity Mitsubishi Lossnay heat recovery ventilation unit.",

  // MEV units
  "0000-4948": "MEV unit. Continuously extracts stale and humid air from wet rooms such as bathrooms and kitchens.",
  "0000-7036": "MEV unit. Central extract unit for removing moist and stale air from wet rooms.",
  "0000-4448": "MEV unit. Whole-house mechanical extract ventilation unit.",

  // controllers / switches
  "443283C": "Controller. Used to control the MVHR unit and adjust settings.",
  "NOTUS/W/BLACK/CONTROLLER": "Controller. Wall-mounted controller for the Notus MVHR unit.",
  "0000-4600": "Controller. Used to control and boost the MEV system.",
  "QV-BoostSw": "Boost switch. Temporarily increases extract airflow during cooking, showering or periods of high moisture.",
  "OZEO-FAULT-IND": "Fault indicator. Gives a visible alert if the ventilation unit develops a fault.",

  // room terminals
  "QV-125AVP-S": "Supply valve. Delivers fresh filtered air into habitable rooms such as bedrooms and living rooms.",
  "QV-125AVP-E": "Extract valve. Removes stale, humid or polluted air from wet rooms.",
  "RONDO-125": "Air valve. Premium room terminal used for supply or extract air.",
  "GPVC125W": "Grille. Air terminal grille used as part of the system air path.",

  // manifolds / plenums
  "QV75-10150TE": "Manifold. Splits airflow between the main unit and multiple radial duct runs.",
  "QV75-12150MTE": "Manifold. Distribution box for multiple radial duct branches.",
  "QV75-15180RE": "Manifold. Larger airflow distribution box for systems with more duct runs.",
  "QV75-PLEN": "Plenum box. Connects radial duct runs to a room valve.",
  "QV90-PLEN": "Plenum box. Connects 90mm radial ducting to a room terminal.",

  // radial duct
  "QV75P-FS": "75mm radial duct. Semi-rigid duct used to carry air between the manifold and room plenums.",
  "QV90PFS": "90mm radial duct. Semi-rigid duct used to carry extract air back to the MEV unit.",

  // rigid duct / fittings
  "P125-2": "Rigid duct pipe. Main duct section used between the unit and key duct components.",
  "P150-2": "Rigid duct pipe. Main duct section for larger airflow systems.",
  "P180M-2": "Rigid duct pipe. Main duct section for higher-capacity systems.",
  "P200M-2": "Rigid duct pipe. Large rigid duct section for high airflow systems.",
  "C125": "Connector. Joins two lengths of rigid duct together.",
  "C150": "Connector. Joins two lengths of 150mm rigid duct.",
  "QV-C180MM": "Connector. Joins two lengths of 180mm rigid duct.",
  "QV-C200MM": "Connector. Joins two lengths of 200mm rigid duct.",
  "B90125": "90 degree bend. Changes the direction of ductwork.",
  "B90150": "90 degree bend. Changes the direction of 150mm ductwork.",
  "QV-B90180M": "90 degree bend. Changes the direction of 180mm ductwork.",
  "QV-B90200M": "90 degree bend. Changes the direction of 200mm ductwork.",
  "T125": "T-piece. Splits or combines airflow in ductwork.",
  "T150": "T-piece. Splits or combines airflow in 150mm ductwork.",
  "T180M": "T-piece. Splits or combines airflow in 180mm ductwork.",
  "T200M": "T-piece. Splits or combines airflow in 200mm ductwork.",
  "QV-R200180M": "Reducer. Adapts one duct size to another.",
  "R150125P": "Reducer. Reduces 150mm duct down to 125mm.",
  "QVRC12590": "Reducer. Connects 125mm fittings to 90mm radial duct components.",

  // accessories
  "QV125F-SILENCER": "Silencer. Reduces noise travelling through the ventilation ductwork.",
  "QV150F-SILENCER": "Silencer. Reduces airflow and fan noise in larger ductwork.",
  "QV180F-SILENCER": "Silencer. Helps reduce noise in larger ventilation systems.",
  "QV200F-SILENCER": "Silencer. Noise reduction component for high-capacity systems.",
  "QVRC125": "Flexible connector. Helps reduce vibration transfer between the unit and ductwork.",
  "QVRC150": "Flexible connector. Helps absorb vibration and ease installation.",
  "CRC170-185": "Flexible connector. Anti-vibration connection for larger duct systems.",
  "CRC180-195": "Flexible connector. Anti-vibration connection for larger duct systems.",
  "QV-SSC125M": "Wall cowl. External terminal that lets air enter or leave through an outside wall.",
  "QV-SSC150M": "Wall cowl. External air terminal for 150mm duct.",
  "QV-SSC180WM": "Wall cowl. External weatherproof terminal for 180mm duct.",
  "QV-SSC200WM": "Wall cowl. External weatherproof terminal for 200mm duct.",
  "BS50": "Builders strap. Used to support and secure ductwork during installation.",
  "ST75": "Foil tape. Used to seal duct joints and improve airtightness.",
  "QV-75SR": "Sealing rings. Help create airtight seals in radial duct connections.",
  "QV90SR": "Sealing rings. Help seal 90mm radial duct connections.",
  "INS25": "Duct insulation. Helps reduce heat loss and condensation on rigid ductwork.",
  "DASEAL": "Sealant. Used to seal duct joints and fittings.",
  "SE380": "Metal sealant. Used to seal joints in metal duct systems."
};

function getKitComponentHelp(line) {
  const sku = String(line?.sku || "").trim();
  return KIT_COMPONENT_HELP[sku] || "";
}


function getEstimatePopup() {
  return $("estimatePopup");
}

function showEstimatePopup() {
  const popup = getEstimatePopup();
  if (!popup) return;
  popup.hidden = false;
  document.body.classList.add("estimate-popup-open");
}

function hideEstimatePopup() {
  const popup = getEstimatePopup();
  if (!popup) return;
  popup.hidden = true;
  document.body.classList.remove("estimate-popup-open");
}

function maybeShowEstimatePopup(mode, calc) {
  if (!calc) return;

  const floorArea = Number(calc?.inputs?.floorArea || 0);
  const unitQty = Number(calc?.results?.unitQty || 0);

  const shouldShow =
    unitQty > 1 ||
    floorArea > 300;

  if (!shouldShow) return;

  const popup = getEstimatePopup();
  if (!popup || popup.dataset.shown === "1") return;

  popup.dataset.shown = "1";
  showEstimatePopup();
}

function bindEstimatePopup() {
  const popup = getEstimatePopup();
  if (!popup) return;

  popup.querySelectorAll("[data-close-estimate-popup]").forEach((el) => {
    el.addEventListener("click", hideEstimatePopup);
  });

  $("estimatePopupClose")?.addEventListener("click", hideEstimatePopup);
}



function $(id) {
  return document.getElementById(id) || null;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function round1(x) {
  return Math.round((Number(x || 0) + Number.EPSILON) * 10) / 10;
}

function fmtMoney(amount, currency = "GBP") {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(n);
  } catch {
    return `£${n.toFixed(2)}`;
  }
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function normaliseUkIePhone(phone, country = "UK") {
  let p = String(phone || "").trim();
  if (!p) return "";

  p = p.replace(/[^\d+]/g, "");

  if (p.startsWith("00")) {
    p = "+" + p.slice(2);
  }

  if (p.startsWith("+44") || p.startsWith("+353")) return p;
  if (p.startsWith("44")) return "+" + p;
  if (p.startsWith("353")) return "+" + p;

  if (p.startsWith("0")) {
    if (country === "IE") return "+353" + p.slice(1);
    return "+44" + p.slice(1);
  }

  return p;
}



async function postQuoteToProxy(payload) {
  const res = await fetch(QUICK_QUOTE_PROXY, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let data = null;

  try {
    data = JSON.parse(text);
  } catch {}

  if (!res.ok) {
    const msg =
      (data && (data.error || data.message)) ||
      (text ? text.slice(0, 400) : `HTTP ${res.status}`);
    throw new Error(msg);
  }

  if (data && data.ok === false) {
    throw new Error(data.error || "Worker returned ok:false");
  }

  if (data && typeof data.status === "number" && data.status >= 400) {
    throw new Error(data.error || `Request failed (${data.status})`);
  }

  return data ?? { ok: true, raw: text };
}

function getConfirmBtn() {
  return null;
}

function ensureViewKitPreviewButton() {
  const kitSection = $("kitPreview");
  const buttonMount = $("quoteButtonMount");

  if (!kitSection || !buttonMount) return;
  if ($("viewKitPreviewBtn")) return;

  const btn = document.createElement("button");
  btn.type = "button";
  btn.id = "viewKitPreviewBtn";
  btn.className = "kit-btn bpc-view-kit-btn";
  btn.textContent = "View kit preview";

  buttonMount.insertAdjacentElement("afterend", btn);

  btn.addEventListener("click", () => {
    kitSection.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function updatePrimaryButtonState() {
  const btn = $("airflowCalcBtn");
  if (!btn) return;
  btn.textContent = leadCaptured ? "Update quote" : "View Quote Now";
}

function formatDateToDdMmYyyy(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const parts = raw.split("-");
  if (parts.length !== 3) return raw;

  const [yyyy, mm, dd] = parts;
  return `${dd}-${mm}-${yyyy}`;
}

function renderError(msg) {
  const result = $("result");
  if (result) {
    result.innerHTML = `<div class="result-wrap"><strong>${escapeHtml(msg)}</strong></div>`;
  }

  const preview = $("kitPreview");
  if (preview) preview.style.display = "none";

  const costEl = $("kitCost");
  if (costEl) costEl.textContent = "Kit total cost: —";

  const confirmBtn = getConfirmBtn();
  if (confirmBtn) confirmBtn.disabled = true;
}

function renderZeroSummary() {
  const result = $("result");
  if (!result) return;

  result.innerHTML = `
    <div class="result-wrap">
      <div class="result-hero">
        <div class="result-hero__label">Required ventilation rate</div>
        <div class="result-hero__value">
          0 L/s
          <span class="result-hero__sub">(0 m³/h)</span>
        </div>
        <div class="result-hero__meta">Governing: —</div>
      </div>
      <div class="rates-grid">
        <div class="rate-card">
          <div class="rate-card__label">Floor area rate</div>
          <div class="rate-card__value">0 <span class="unit">L/s</span></div>
        </div>
        <div class="rate-card">
          <div class="rate-card__label">Bedrooms rate</div>
          <div class="rate-card__value">0 <span class="unit">L/s</span></div>
        </div>
        <div class="rate-card">
          <div class="rate-card__label">Wet rooms rate</div>
          <div class="rate-card__value">0 <span class="unit">L/s</span></div>
        </div>
      </div>
    </div>
  `;
}

function renderMvhrSummary(results = {}) {
  const result = $("result");
  if (!result) return;

  result.innerHTML = `
    <div class="result-wrap">
      <div class="result-hero">
        <div class="result-hero__label">Required ventilation rate</div>
        <div class="result-hero__value">
          ${round1(results.governingLs)} L/s
          <span class="result-hero__sub">(${Number(results.governingM3h || 0).toFixed(0)} m³/h)</span>
        </div>
        <div class="result-hero__meta">Governing: <strong>${escapeHtml(results.governingLabel || "—")}</strong></div>
      </div>
      <div class="rates-grid">
        <div class="rate-card">
          <div class="rate-card__label">Floor area rate</div>
          <div class="rate-card__value">${round1(results.floorAreaRateLs)} <span class="unit">L/s</span></div>
        </div>
        <div class="rate-card">
          <div class="rate-card__label">Bedrooms rate</div>
          <div class="rate-card__value">${round1(results.bedroomsRateLs)} <span class="unit">L/s</span></div>
        </div>
        <div class="rate-card">
          <div class="rate-card__label">Wet rooms rate</div>
          <div class="rate-card__value">${round1(results.wetRoomsRateLs)} <span class="unit">L/s</span></div>
        </div>
      </div>
    </div>
  `;
}

function renderMevSummary(results = {}) {
  const result = $("result");
  if (!result) return;

  result.innerHTML = `
    <div class="result-wrap">
      <div class="result-hero">
        <div class="result-hero__label">Required ventilation rate</div>
        <div class="result-hero__value">
          ${round1(results.governingLs)} L/s
          <span class="result-hero__sub">(${Number(results.governingM3h || 0).toFixed(0)} m³/h)</span>
        </div>
        <div class="result-hero__meta">Governing: <strong>MEV airflow target</strong></div>
      </div>
      <div class="rates-grid">
        <div class="rate-card">
          <div class="rate-card__label">Floor area rate</div>
          <div class="rate-card__value">— <span class="unit">L/s</span></div>
        </div>
        <div class="rate-card">
          <div class="rate-card__label">Bedrooms rate</div>
          <div class="rate-card__value">— <span class="unit">L/s</span></div>
        </div>
        <div class="rate-card">
          <div class="rate-card__label">Wet rooms rate</div>
          <div class="rate-card__value">— <span class="unit">L/s</span></div>
        </div>
      </div>
    </div>
  `;
}

function resetOutputs() {
  const choiceEl = $("mvhrChoice");
  const previewEl = $("kitPreview");
  const costEl = $("kitCost");
  const bodyEl = $("kitPreviewBody");
  const headEl = $("kitPreviewHead");
  const confirmBtn = getConfirmBtn();

  renderZeroSummary();

  if (choiceEl) choiceEl.innerHTML = "";
  if (previewEl) previewEl.style.display = "none";
  if (bodyEl) bodyEl.innerHTML = "";
  if (headEl) headEl.innerHTML = "";
  if (costEl) costEl.textContent = "Kit total cost: —";
  if (confirmBtn) confirmBtn.disabled = true;

  lastCalc = null;
  lastKitLines = [];

  updatePrimaryButtonState();
}

function getQuoteButtonHtml() {
  return `<button id="airflowCalcBtn" type="button" class="kit-btn">View Quote Now</button>`;
}

function getMvhrInputsHtml() {
  return `
    <div class="bpc-input-section">
      <h3 class="bpc-input-section-title">Property details</h3>

      <label>Total Project Floor Area (m²)</label>
      <input type="number" id="floorArea" placeholder="e.g. 95">

      <label>No. of Bedrooms</label>
      <input type="number" id="bedrooms" placeholder="e.g. 2">

      <label>No. of Living Rooms</label>
      <input type="number" id="livingRooms" placeholder="e.g. 1">

      <label>Other Habitable Rooms</label>
      <input type="number" id="otherRooms" placeholder="e.g. 1">

      <label>No. of Kitchens</label>
      <input type="number" id="kitchens" placeholder="e.g. 1">

      <label>No. of Bathrooms / Ensuites</label>
      <input type="number" id="bathrooms" placeholder="e.g. 2">

      <label>No. of Utility Rooms</label>
      <input type="number" id="utility" placeholder="e.g. 1">

      <label>No. of Water Closets</label>
      <input type="number" id="wcs" placeholder="e.g. 1">
    </div>

    <div class="bpc-input-section bpc-input-section--options">
      <h3 class="bpc-input-section-title">Kit options</h3>

      <label>Would you like upgraded air valves?</label>
      <select id="upgradedValves">
        <option value="no" selected>No, standard valves are fine</option>
        <option value="yes">Yes, include upgraded air valves</option>
      </select>

      <label>Does the property have 1st floor concrete floors?</label>
      <select id="concreteFloors">
        <option value="no" selected>No</option>
        <option value="yes">Yes</option>
      </select>

      <p class="bpc-option-note">
        Concrete floors can require extra distribution boxes and pipework, so this should be selected before the quote is created.
      </p>
    </div>
  `;
}

function getMevInputsHtml() {
  return `
    <div class="bpc-input-section">
      <h3 class="bpc-input-section-title">Property details</h3>

      <label>Total Project Floor Area (m²)</label>
      <input type="number" id="floorArea" placeholder="e.g. 95">

      <label>No. of Kitchens</label>
      <input type="number" id="kitchens" placeholder="e.g. 1">

      <label>No. of Bathrooms / Ensuites</label>
      <input type="number" id="bathrooms" placeholder="e.g. 2">

      <label>No. of Utility Rooms</label>
      <input type="number" id="utility" placeholder="e.g. 1">

      <label>No. of Water Closets</label>
      <input type="number" id="wcs" placeholder="e.g. 1">
    </div>

    <div class="bpc-input-section bpc-input-section--options">
      <h3 class="bpc-input-section-title">Kit options</h3>

      <label>Would you like upgraded air valves?</label>
      <select id="upgradedValves">
        <option value="no" selected>No, standard valves are fine</option>
        <option value="yes">Yes, include upgraded air valves</option>
      </select>

      <label>Install location</label>
      <select id="installRegion">
        <option value="uk" selected>UK</option>
        <option value="ie">Ireland</option>
      </select>

      <p class="bpc-option-note">
        Ireland installs can include extra fault indication requirements, so this should be selected before the quote is created.
      </p>
    </div>
  `;
}

function collectInputs(mode) {
  const floorArea = Number($("floorArea")?.value || 0);

  if (mode === "mvhr") {
    return {
      floorArea,
      bedrooms: Number($("bedrooms")?.value || 0),
      livingRooms: Number($("livingRooms")?.value || 0),
      otherRooms: Number($("otherRooms")?.value || 0),
      kitchens: Number($("kitchens")?.value || 0),
      bathrooms: Number($("bathrooms")?.value || 0),
      utility: Number($("utility")?.value || 0),
      wcs: Number($("wcs")?.value || 0),
    };
  }

  return {
    floorArea,
    kitchens: Number($("kitchens")?.value || 0),
    bathrooms: Number($("bathrooms")?.value || 0),
    utility: Number($("utility")?.value || 0),
    wcs: Number($("wcs")?.value || 0),
  };
}

function collectOptions(mode) {
  const suggested = lastPlanSuggestedOptions || {};

  if (mode === "mvhr") {
    return {
      selectedUnitSku: String($("mvhrSelect")?.value || ""),
      upgradedValves: String($("upgradedValves")?.value || suggested.upgradedValves || "no"),
      concreteFloors: String(
        $("concreteFloors")?.value ||
        (suggested.concreteFloors && suggested.concreteFloors !== "unknown" ? suggested.concreteFloors : "no")
      ),
    };
  }

  return {
    selectedUnitSku: String($("mevSelect")?.value || ""),
    upgradedValves: String($("upgradedValves")?.value || suggested.upgradedValves || "no"),
    installRegion: String($("installRegion")?.value || suggested.installRegion || "uk"),
  };
}

function bindChoiceRebuilds(mode) {
  if (mode === "mvhr") {
    $("mvhrSelect")?.addEventListener("change", () => buildFromServer("mvhr"));
    return;
  }

  $("mevSelect")?.addEventListener("change", () => buildFromServer("mev"));
}

function renderChoiceUI(mode, choices, options) {
  const el = $("mvhrChoice");
  if (!el) return;

  const units = Array.isArray(choices?.units) ? choices.units : [];

  if (mode === "mvhr") {
    el.innerHTML = `
      <label style="font-weight:600; display:block; margin:10px 0 6px;">Choose MVHR Unit</label>
      <select id="mvhrSelect" style="width:100%; padding:12px; height:48px; border-radius:6px; border:1px solid #ccc; font-size:16px;">
        ${units.map((u) => `<option value="${escapeHtml(u.sku)}">${escapeHtml(u.name || u.sku)}</option>`).join("")}
      </select>
    `;

    if ($("mvhrSelect")) {
      $("mvhrSelect").value = String(options?.selectedUnitSku || units[0]?.sku || "");
    }
  } else {
    el.innerHTML = `
      <label style="font-weight:600; display:block; margin:10px 0 6px;">Choose MEV Unit</label>
      <select id="mevSelect" style="width:100%; padding:12px; height:48px; border-radius:6px; border:1px solid #ccc; font-size:16px;">
        ${units.map((u) => `<option value="${escapeHtml(u.sku)}">${escapeHtml(u.name || u.sku)}</option>`).join("")}
      </select>
    `;

    if ($("mevSelect")) {
      $("mevSelect").value = String(options?.selectedUnitSku || units[0]?.sku || "");
    }
  }

  bindChoiceRebuilds(mode);
}

function renderKit(lines, totals) {
  const preview = $("kitPreview");
  const body = $("kitPreviewBody");
  const head = $("kitPreviewHead");
  const costEl = $("kitCost");
  const confirmBtn = getConfirmBtn();

  if (preview) preview.style.display = "block";
  ensureViewKitPreviewButton();

  if (head) {
    head.innerHTML = `
      <tr>
        <th class="img-col"></th>
        <th align="left">Item</th>
        <th align="left">SKU</th>
        <th class="qty-col" style="text-align:right;">Qty</th>
        <th align="right">Total</th>
      </tr>
    `;
  }

  if (body) {
    body.innerHTML = `
      ${(lines || [])
        .map((line) => {
          const helpText = getKitComponentHelp(line);
          const helpId = `kit-help-${makeSafeDomId(line.sku)}`;

          const img = line.image
            ? `<img class="thumb" src="${escapeHtml(line.image)}" alt="" loading="lazy">`
            : `<div class="thumb" aria-hidden="true"></div>`;

          const lineTotal = (Number(line.price) || 0) * (Number(line.qty) || 0);

          return `
            <tr>
              <td class="cImg">
                <div class="kit-media-stack">
                  ${img}
                  ${
                    helpText
                      ? `
                        <button
                          type="button"
                          class="kit-help-toggle kit-help-toggle--image"
                          aria-label="What does this component do?"
                          aria-expanded="false"
                          aria-controls="${helpId}"
                        >
                          ?
                        </button>
                      `
                      : ``
                  }
                </div>
              </td>

              <td>
                <div class="kit-item-name">${escapeHtml(line.title || line.sku)}</div>

                ${
                  helpText
                    ? `
                      <div
                        id="${helpId}"
                        class="kit-help-panel"
                        hidden
                      >
                        ${escapeHtml(helpText)}
                      </div>
                    `
                    : ``
                }

                ${!line.variantId ? `<div class="hint" style="color:#b00020">SKU not found in Shopify</div>` : ``}
                ${line.available === false ? `<div class="hint" style="color:#b00020">Out of stock</div>` : ``}
                ${line.available === null ? `<div class="hint" style="color:#666">Stock unknown</div>` : ``}
              </td>

              <td class="cSku">${escapeHtml(line.sku)}</td>
              <td class="cQty" style="text-align:right">${Number(line.qty || 0)}</td>
              <td class="cPrice" style="text-align:right">${fmtMoney(lineTotal, line.currency || totals?.currency || "GBP")}</td>
            </tr>
          `;
        })
        .join("")}
    `;

    bindKitHelpToggles();
  }

  const currency = totals?.currency || lines?.find((x) => x.currency)?.currency || "GBP";
  const exVat = Number(totals?.exVat || 0);
  const incVat = Number(totals?.incVat || exVat * 1.2);

  if (costEl) {
    costEl.innerHTML = `
      <div class="kit-cost-lines">
        <div class="kit-cost-ex">
          Kit total cost (ex VAT):
          <span class="kit-total-amount">${fmtMoney(exVat, currency)}</span>
        </div>
        <div class="kit-cost-inc">
          inc VAT:
          <strong>${fmtMoney(incVat, currency)}</strong>
        </div>
      </div>
    `;
  }

  const anyMissing = (lines || []).some((x) => !x.variantId);
  if (confirmBtn) confirmBtn.disabled = anyMissing;
}

function makeSafeDomId(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function bindKitHelpToggles() {
  document.querySelectorAll(".kit-help-toggle").forEach((btn) => {
    if (btn.dataset.bound === "1") return;
    btn.dataset.bound = "1";

    btn.addEventListener("click", () => {
      const targetId = btn.getAttribute("aria-controls");
      const panel = targetId ? document.getElementById(targetId) : null;
      if (!panel) return;

      const isOpen = btn.getAttribute("aria-expanded") === "true";
      btn.setAttribute("aria-expanded", isOpen ? "false" : "true");
      panel.hidden = isOpen;
    });
  });
}

async function buildFromServer(mode) {
  const runId = ++calcRunId;

  const body = $("kitPreviewBody");
  const preview = $("kitPreview");
  const costEl = $("kitCost");
  const confirmBtn = getConfirmBtn();
  const btn = $("airflowCalcBtn");
  const oldText = btn?.textContent || "Get quote";

  try {
    const inputs = collectInputs(mode);

    if (!inputs.floorArea || inputs.floorArea <= 0) {
      renderError("Please enter a valid floor area");
      return;
    }

    if (mode === "mev") {
      const extractRooms =
        Number(inputs.kitchens || 0) +
        Number(inputs.bathrooms || 0) +
        Number(inputs.utility || 0) +
        Number(inputs.wcs || 0);

      if (extractRooms <= 0) {
        renderError("Please enter at least one extract room.");
        return;
      }
    }

    const options = collectOptions(mode);
    let payload;

    if (!leadCaptured) {
    const nameEl = $("qqName") || $("quoteName");
const emailEl = $("qqEmail") || $("quoteEmail");
const stageEl = $("qqStage");
const installDateEl = $("qqInstallDate");
const phoneEl = $("qqPhone");
const phoneCountryEl = $("qqPhoneCountry");

const name = String(nameEl?.value || "").trim();
const email = String(emailEl?.value || "").trim();
const stage = String(stageEl?.value || "").trim();
const expectedInstallDate = formatDateToDdMmYyyy(installDateEl?.value || "");
const phoneCountry = String(phoneCountryEl?.value || "UK").toUpperCase();
const phone = normaliseUkIePhone(phoneEl?.value || "", phoneCountry);

      if (!name) {
        alert("Please enter your name.");
        nameEl?.focus();
        return;
      }

      if (!isValidEmail(email)) {
        alert("Please enter a valid email address.");
        emailEl?.focus();
        return;
      }

      if (!stage) {
        alert("Please select your construction stage.");
        stageEl?.focus();
        return;
      }


     payload = {
  action: "submit_lead_and_build",
  logged_in_customer_id: window.BPC_LOGGED_IN_CUSTOMER_ID || "",
  systemType: mode,
  name,
  email,
  customer: {
    name,
    email,
    stage,
    phone,
    phoneCountry,
    expectedInstallDate
  },
  inputs,
  options,
  planAnalysis: null,
  extra: {
    phone,
    phoneCountry,
    expectedInstallDate
  },
  meta: {
    source: CRM_SOURCE_TAG,
    page: location.href,
    systemType: mode,
    stage,
    phoneCountry,
    expectedInstallDate
  }
};
    } else {
      payload = {
  action: "build_kit",
  systemType: mode,
  inputs,
  options,
  planAnalysis: null,
};
    }

    if (preview) preview.style.display = "block";
    if (body) body.innerHTML = `<tr><td colspan="5" class="empty">Building kit…</td></tr>`;
    if (costEl) costEl.textContent = "Kit total cost: Calculating…";
    if (confirmBtn) confirmBtn.disabled = true;

    if (btn) {
      btn.disabled = true;
      btn.textContent = "Building…";
    }

    const resp = await postQuoteToProxy(payload);
    if (runId !== calcRunId) return;

    if (!leadCaptured && payload.action === "submit_lead_and_build" && resp?.ok) {
  leadCaptured = true;

  const tempCalc = {
    type: mode,
    quoteRef: String(resp.quoteRef || resp?.meta?.quoteRef || ""),
    inputs: resp.inputs || inputs,
    options: resp.options || options,
    results: resp.results || {},
    totals: resp?.kit?.totals || {},
  };

  const floorArea = Number(tempCalc?.inputs?.floorArea || 0);
  const unitQty = Number(tempCalc?.results?.unitQty || 0);
  const needsDesignReview = unitQty > 1 || floorArea > 300;

  if (needsDesignReview) {
    lastCalc = tempCalc;
    lastKitLines = Array.isArray(resp?.kit?.lines) ? resp.kit.lines : [];

    if (mode === "mvhr") renderMvhrSummary(lastCalc.results);
    else renderMevSummary(lastCalc.results);

    renderChoiceUI(mode, resp.choices || {}, resp.options || options);
    renderKit(lastKitLines, resp?.kit?.totals || {});
    maybeShowEstimatePopup(mode, lastCalc);

    return;
  }

  const redirectUrl = String(resp.redirectUrl || resp.thankYouPageUrl || "").trim();

  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }
}

    lastCalc = {
  type: mode,
  quoteRef: String(resp.quoteRef || resp?.meta?.quoteRef || ""),
  inputs: resp.inputs || inputs,
  options: resp.options || options,
  results: resp.results || {},
  totals: resp?.kit?.totals || {},
};

    lastKitLines = Array.isArray(resp?.kit?.lines) ? resp.kit.lines : [];

    if (mode === "mvhr") renderMvhrSummary(lastCalc.results);
    else renderMevSummary(lastCalc.results);

    renderChoiceUI(mode, resp.choices || {}, resp.options || options);
    renderKit(lastKitLines, resp?.kit?.totals || {});
    maybeShowEstimatePopup(mode, lastCalc);
  } catch (err) {
    console.error(err);
    renderError(err?.message || "Something went wrong.");
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = leadCaptured ? "Update quote" : oldText;
    }
  }
}

async function addKitToCart() {
  try {
    if (!lastKitLines?.length) {
      alert("Please calculate a kit first.");
      return;
    }

    const missing = lastKitLines.filter((x) => !x.variantId);

    if (missing.length) {
      alert(
        "Cannot add to cart because these SKUs were not found in Shopify:\n\n" +
        missing.map((x) => `- ${x.sku}`).join("\n")
      );
      return;
    }

    const items = lastKitLines.map((x) => ({
      id: Number(x.variantId),
      quantity: Number(x.qty),
      sku: String(x.sku || ""),
      title: String(x.title || x.sku || ""),
    }));

    console.log("Instant quote cart items:", items);

    // First try the normal batch add.
    const addRes = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items.map((x) => ({
          id: x.id,
          quantity: x.quantity,
        })),
      }),
    });

    if (!addRes.ok) {
      let batchMsg = "Could not add kit to cart.";
      try {
        const data = await addRes.json();
        console.error("Batch add failed:", data);
        if (data?.description) batchMsg = data.description;
        if (data?.message) batchMsg = data.message;
      } catch {}

      console.warn("Batch add failed. Testing each item one by one...");

      const failed = [];

      // Clear cart first so the test does not partly add items.
      await fetch("/cart/clear.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      for (const item of items) {
        const singleRes = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: item.id,
            quantity: item.quantity,
          }),
        });

        if (!singleRes.ok) {
          let reason = "Unknown Shopify cart error";

          try {
            const data = await singleRes.json();
            console.error("Single item failed:", item, data);
            reason = data?.description || data?.message || JSON.stringify(data);
          } catch {
            reason = await singleRes.text().catch(() => reason);
          }

          failed.push({
            ...item,
            reason,
          });
        }
      }

      // Clear cart again because some single tests may have succeeded.
      await fetch("/cart/clear.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (failed.length) {
        alert(
          "Shopify rejected these item(s):\n\n" +
          failed
            .map((x) => `- ${x.sku} | ${x.title}\n  Variant ID: ${x.id}\n  Reason: ${x.reason}`)
            .join("\n\n") +
          "\n\nThis usually means the product/variant is not available to Online Store, the current market, or is not purchasable."
        );
        return;
      }

      throw new Error(batchMsg);
    }

    const attrs = {
      attributes: {
        quote_source: "instant_quote",
        quote_system: String(lastCalc?.type || systemType || ""),
        quote_ref: String(lastCalc?.quoteRef || ""),
        quote_page: window.location.href,
        quote_customer_name: String(document.getElementById("qqName")?.value || "").trim(),
        quote_customer_email: String(document.getElementById("qqEmail")?.value || "").trim(),
      },
    };

    const attrRes = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(attrs),
    });

    if (!attrRes.ok) {
      throw new Error("Kit added, but could not stamp cart attributes.");
    }

    window.location.href = "/checkout?instant=1";
  } catch (err) {
    console.error(err);
    alert(err?.message || "Could not add kit to cart.");
  }
}

window.addKitToCart = addKitToCart;

function renderCalculator(mode = "mvhr") {
  const mount = $("calculatorMount");
  const buttonMount = $("quoteButtonMount");
  if (!mount) return;

  resetOutputs();
  mount.innerHTML = mode === "mev" ? getMevInputsHtml() : getMvhrInputsHtml();

  if (buttonMount) {
    buttonMount.innerHTML = getQuoteButtonHtml();
  }

  const btn = $("airflowCalcBtn");
  btn?.addEventListener("click", () => buildFromServer(mode));

  updatePrimaryButtonState();
}

/* =========================================================
   AI PLAN READER HELPERS
========================================================= */

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the selected file."));

    reader.readAsDataURL(file);
  });
}

function setPlanAiStatus(message, type = "") {
  const el = $("bpcPlanAiStatus");
  if (!el) return;

  el.className = "bpc-plan-ai-status";
  if (type === "error") el.classList.add("is-error");
  if (type === "success") el.classList.add("is-success");

  el.textContent = message || "";
}

function setInputValue(id, value) {
  const el = $(id);
  if (!el) return;
  el.value = value == null ? "" : String(value);
}

function applyPlanInputsToForm(planAnalysis) {
  const inputs = planAnalysis?.quote_inputs || {};
  const options = planAnalysis?.options || {};

  lastPlanSuggestedOptions = options || null;

  if (!inputs || typeof inputs !== "object") return;

  const detectedSystem =
    String(inputs.systemType || systemType || "mvhr").toLowerCase() === "mev"
      ? "mev"
      : "mvhr";

  const topSel = $("systemTypeTop");

  if (topSel && topSel.value !== detectedSystem) {
    topSel.value = detectedSystem;
    systemType = detectedSystem;
    renderCalculator(systemType);
  }

  setInputValue("floorArea", inputs.floorArea || "");

  if (detectedSystem === "mvhr") {
    setInputValue("bedrooms", inputs.bedrooms || 0);
    setInputValue("livingRooms", inputs.livingRooms || 0);
    setInputValue("otherRooms", inputs.otherRooms || 0);
  }

  setInputValue("kitchens", inputs.kitchens || 0);
  setInputValue("bathrooms", inputs.bathrooms || 0);
  setInputValue("utility", inputs.utility || 0);
  setInputValue("wcs", inputs.wcs || 0);
}

function renderPlanAiSummary(planAnalysis) {
  const summaryEl = $("bpcPlanAiSummary");
  const roomsEl = $("bpcPlanAiRooms");

  if (!summaryEl || !roomsEl) return;

  const project = planAnalysis?.project_summary || {};
  const inputs = planAnalysis?.quote_inputs || {};
  const warnings = Array.isArray(planAnalysis?.warnings) ? planAnalysis.warnings : [];
  const missing = Array.isArray(planAnalysis?.missing_info) ? planAnalysis.missing_info : [];
  const rooms = Array.isArray(planAnalysis?.rooms) ? planAnalysis.rooms : [];

  summaryEl.hidden = false;
  summaryEl.innerHTML = `
    <div><strong>Plan quality:</strong> ${escapeHtml(project.plan_quality || "medium")}</div>
    <div><strong>Confidence:</strong> ${Math.round(Number(project.confidence || 0) * 100)}%</div>
    <div><strong>Floor area:</strong> ${escapeHtml(inputs.floorArea || project.floor_area_m2 || "—")} m²</div>
    <div><strong>Detected inputs:</strong>
      ${escapeHtml(inputs.bedrooms || 0)} bedrooms,
      ${escapeHtml(inputs.kitchens || 0)} kitchen(s),
      ${escapeHtml(inputs.bathrooms || 0)} bathroom/ensuite(s),
      ${escapeHtml(inputs.utility || 0)} utility,
      ${escapeHtml(inputs.wcs || 0)} WC(s)
    </div>
    ${
      warnings.length || missing.length
        ? `<div style="margin-top:8px;"><strong>Review needed:</strong><br>${[...warnings, ...missing].map((x) => `• ${escapeHtml(x)}`).join("<br>")}</div>`
        : `<div style="margin-top:8px;"><strong>Review needed:</strong> Please still check the room list and numbers before building the kit.</div>`
    }
  `;

  if (!rooms.length) {
    roomsEl.hidden = true;
    roomsEl.innerHTML = "";
    return;
  }

  roomsEl.hidden = false;
  roomsEl.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>Floor</th>
          <th>Room</th>
          <th>Type</th>
          <th>Area</th>
          <th>Role</th>
          <th>Confidence</th>
        </tr>
      </thead>
      <tbody>
        ${rooms
          .map((room) => {
            const conf = Number(room.confidence || 0);
            return `
              <tr>
                <td>${escapeHtml(room.floor || "")}</td>
                <td>${escapeHtml(room.room_name || "")}</td>
                <td>${escapeHtml(room.room_type || "")}</td>
                <td>${room.area_m2 ? `${escapeHtml(room.area_m2)} m²` : "—"}</td>
                <td>${escapeHtml(room.ventilation_role || "")}</td>
                <td>
                  <span class="bpc-plan-ai-confidence ${conf < 0.7 ? "is-low" : ""}">
                    ${Math.round(conf * 100)}%
                  </span>
                </td>
              </tr>
            `;
          })
          .join("")}
      </tbody>
    </table>
  `;
}

async function analysePlanUpload() {
  if (!window.BPC_PLAN_AI_ACCESS) {
  setPlanAiStatus("AI plan quote beta is not enabled for this account.", "error");
  return;
}

  const fileEl = $("bpcPlanAiFile");
  const btn = $("bpcAnalysePlanBtn");
  const file = fileEl?.files?.[0];

  if (!file) {
    setPlanAiStatus("Please choose a PDF or image plan first.", "error");
    return;
  }

  const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
  const isPdfName = /\.pdf$/i.test(file.name || "");

  if (!allowed.includes(file.type) && !isPdfName) {
    setPlanAiStatus("Please upload a PDF, JPG, PNG or WebP file.", "error");
    return;
  }

  const maxSize = isPdfName || file.type === "application/pdf" ? 20 * 1024 * 1024 : 8 * 1024 * 1024;
  if (file.size > maxSize) {
    setPlanAiStatus("That file is too large. Please use a smaller PDF/image.", "error");
    return;
  }

  const oldText = btn?.textContent || "Analyse plan";

  try {
    if (btn) {
      btn.disabled = true;
      btn.textContent = "Reading plan…";
    }

    setPlanAiStatus("Uploading and analysing plan. This may take a moment…");

    const dataUrl = await fileToDataUrl(file);

    const resp = await postQuoteToProxy({
  action: "analyse_plan",
  logged_in_customer_id: window.BPC_LOGGED_IN_CUSTOMER_ID || "",
  systemType,
  country: String($("qqPhoneCountry")?.value || "UK"),
  file: {
    name: file.name,
    type: file.type || (isPdfName ? "application/pdf" : ""),
    size: file.size,
    dataUrl,
  },
  meta: {
    source: "ai_plan_reader_beta",
    page: location.href,
    systemType,
  },
});

    lastPlanAnalysis = resp?.planAnalysis || null;

    if (!lastPlanAnalysis) {
      throw new Error("No plan analysis was returned.");
    }

   renderPlanAiSummary(lastPlanAnalysis);

// Do not auto-fill quote inputs for now.
// AI plan reading is review-only until measurement/room detection is more reliable.
// applyPlanInputsToForm(lastPlanAnalysis);

setPlanAiStatus("Plan read complete. Please review the detected rooms below. Quote inputs have not been auto-filled.", "success");
  } catch (err) {
    console.error(err);
    setPlanAiStatus(err?.message || "Could not analyse the plan.", "error");
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = oldText;
    }
  }
}

function bindPlanAiUpload() {
  if (!window.BPC_PLAN_AI_ACCESS) return;

  const btn = $("bpcAnalysePlanBtn");
  if (!btn || btn.dataset.bound === "1") return;

  btn.dataset.bound = "1";
  btn.addEventListener("click", analysePlanUpload);
}

let bernieHistory = [];

function getBernieSnapshot() {
  return {
    page: "instant_quote_form",
    systemType,
    inputs: collectInputs(systemType),
    options: collectOptions(systemType),
    lastCalc,
    kitLines: Array.isArray(lastKitLines)
      ? lastKitLines.slice(0, 30).map((line) => ({
          sku: String(line.sku || ""),
          title: String(line.title || line.sku || "Item"),
          qty: Number(line.qty || 0),
          price: line.price ?? null,
          available: line.available ?? null,
          variantId: line.variantId || null
        }))
      : [],
    leadCaptured,
    planAnalysis: lastPlanAnalysis
      ? {
          hasPlanAnalysis: true,
          project_summary: lastPlanAnalysis.project_summary || {},
          quote_inputs: lastPlanAnalysis.quote_inputs || {},
          warnings: lastPlanAnalysis.warnings || [],
          missing_info: lastPlanAnalysis.missing_info || []
        }
      : null,
    quoteMeta: {
      pageUrl: window.location.href
    }
  };
}

function getBernieAvatarHtml() {
  const avatarImg = document.querySelector(".bpc-bernie-avatar-img");

  if (avatarImg && avatarImg.getAttribute("src")) {
    return `<img src="${escapeHtml(avatarImg.getAttribute("src"))}" alt="">`;
  }

  return `<span>🦦</span>`;
}

function addBernieMessage(role, text) {
  const messages = $("bpcBernieMessages");
  if (!messages) return null;

  const row = document.createElement("div");
  row.className =
    role === "user"
      ? "bpc-bernie-msg-row bpc-bernie-msg-row--user"
      : "bpc-bernie-msg-row bpc-bernie-msg-row--bot";

  if (role !== "user") {
    const avatar = document.createElement("div");
    avatar.className = "bpc-bernie-mini-avatar";
    avatar.innerHTML = getBernieAvatarHtml();
    row.appendChild(avatar);
  }

  const div = document.createElement("div");
  div.className =
    role === "user"
      ? "bpc-bernie-msg bpc-bernie-msg--user"
      : "bpc-bernie-msg bpc-bernie-msg--bot";

  div.textContent = String(text || "");
  row.appendChild(div);

  messages.appendChild(row);
  messages.scrollTop = messages.scrollHeight;

  return row;
}

function addBernieTyping() {
  const messages = $("bpcBernieMessages");
  if (!messages) return null;

  removeBernieTyping();

  const row = document.createElement("div");
  row.className = "bpc-bernie-msg-row bpc-bernie-msg-row--bot";
  row.id = "bpcBernieTypingRow";

  const avatar = document.createElement("div");
  avatar.className = "bpc-bernie-mini-avatar";
  avatar.innerHTML = getBernieAvatarHtml();

  const typing = document.createElement("div");
  typing.className = "bpc-bernie-typing";
  typing.setAttribute("aria-label", "Bernie is typing");
  typing.innerHTML = `<span></span><span></span><span></span>`;

  row.appendChild(avatar);
  row.appendChild(typing);

  messages.appendChild(row);
  messages.scrollTop = messages.scrollHeight;

  return row;
}

function removeBernieTyping() {
  const typing = $("bpcBernieTypingRow");
  if (typing) typing.remove();
}

async function askBernie(promptText) {
  const input = $("bpcBernieInput");
  const askBtn = $("bpcBernieAskBtn");

  const message = String(promptText || input?.value || "").trim();
  if (!message) return;

  if (input) input.value = "";

  addBernieMessage("user", message);

  bernieHistory.push({
    role: "user",
    content: message
  });

  try {
    if (askBtn) {
      askBtn.disabled = true;
      askBtn.setAttribute("aria-busy", "true");
    }

    addBernieTyping();

    const res = await fetch("/apps/commercial-ai", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        assistant: "bernie",
        message,
        history: bernieHistory.slice(-8),
        snapshot: getBernieSnapshot(),
        context: {
          assistantName: "Bernie",
          pageType: "instant_quote_form",
          tone: "friendly quote helper",
          purpose: "Help the customer complete the MVHR/MEV instant quote form, understand their entered data, explain options, explain airflow and explain the generated quote kit if available. Do not answer unrelated questions or random SKU checks unless the SKU is part of the current kit snapshot.",
          currentUrl: window.location.href
        }
      })
    });

    const data = await res.json().catch(() => null);

    removeBernieTyping();

    if (!res.ok || !data || data.ok === false) {
      throw new Error(data?.error || "Bernie could not answer just now.");
    }

    const answer = data.answer || "Sorry, I could not answer that.";
    addBernieMessage("bot", answer);

    bernieHistory.push({
      role: "assistant",
      content: answer
    });

    bernieHistory = bernieHistory.slice(-10);
  } catch (err) {
    console.error(err);
    removeBernieTyping();

    addBernieMessage(
      "bot",
      err?.message || "Sorry, Bernie could not answer just now."
    );
  } finally {
    if (askBtn) {
      askBtn.disabled = false;
      askBtn.removeAttribute("aria-busy");
    }
  }
}

function bindBernie() {
  const card = $("bpcBernieCard");
  const input = $("bpcBernieInput");
  const askBtn = $("bpcBernieAskBtn");
  const minimiseBtn = document.querySelector(".bpc-bernie-minimise");

  if (askBtn && askBtn.dataset.bound !== "1") {
    askBtn.dataset.bound = "1";
    askBtn.addEventListener("click", () => askBernie());
  }

  if (input && input.dataset.bound !== "1") {
    input.dataset.bound = "1";
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        askBernie();
      }
    });
  }

  document.querySelectorAll("[data-bernie-prompt]").forEach((btn) => {
    if (btn.dataset.bound === "1") return;

    btn.dataset.bound = "1";
    btn.addEventListener("click", () => {
      askBernie(btn.getAttribute("data-bernie-prompt") || "");
    });
  });

  if (minimiseBtn && minimiseBtn.dataset.bound !== "1") {
    minimiseBtn.dataset.bound = "1";

    minimiseBtn.addEventListener("click", (event) => {
      event.stopPropagation();

      if (!card) return;

      card.classList.toggle("is-minimised");

      minimiseBtn.textContent = card.classList.contains("is-minimised") ? "+" : "−";
      minimiseBtn.setAttribute(
        "aria-label",
        card.classList.contains("is-minimised") ? "Open Bernie" : "Minimise Bernie"
      );
    });
  }

  if (card && card.dataset.bound !== "1") {
    card.dataset.bound = "1";

    card.addEventListener("click", () => {
      if (!card.classList.contains("is-minimised")) return;

      card.classList.remove("is-minimised");

      if (minimiseBtn) {
        minimiseBtn.textContent = "−";
        minimiseBtn.setAttribute("aria-label", "Minimise Bernie");
      }
    });
  }

  const bernieToggleBtn = $("bpcBernieToggleBtn");
const bernieShell = $("bpcBernieShell");

if (bernieToggleBtn && bernieToggleBtn.dataset.bound !== "1") {
  bernieToggleBtn.dataset.bound = "1";

  bernieToggleBtn.addEventListener("click", () => {
    if (!bernieShell) return;

    const isOpen = bernieShell.classList.toggle("is-open");
    bernieToggleBtn.setAttribute("aria-expanded", isOpen ? "true" : "false");

    if (isOpen) {
      setTimeout(() => {
        $("bpcBernieInput")?.focus();
      }, 150);
    }
  });
}
}

window.BPC_BERNIE_GET_SNAPSHOT = function () {
  return {
    page: "instant_quote_form",
    systemType,
    inputs: collectInputs(systemType),
    options: collectOptions(systemType),
    lastCalc,
    kitLines: Array.isArray(lastKitLines)
      ? lastKitLines.slice(0, 60).map((line) => ({
          sku: String(line.sku || ""),
          title: String(line.title || line.sku || "Item"),
          qty: Number(line.qty || line.quantity || 0),
          price: line.price ?? line.unitPrice ?? line.unit_price ?? null,
          currency: line.currency || lastCalc?.totals?.currency || "GBP",
          available: line.available ?? null,
          variantId: line.variantId || line.variant_id || null,
          image: line.image || ""
        }))
      : [],
    leadCaptured,
    planAnalysis: lastPlanAnalysis
      ? {
          hasPlanAnalysis: true,
          project_summary: lastPlanAnalysis.project_summary || {},
          quote_inputs: lastPlanAnalysis.quote_inputs || {},
          warnings: lastPlanAnalysis.warnings || [],
          missing_info: lastPlanAnalysis.missing_info || []
        }
      : null,
    quoteMeta: {
      pageUrl: window.location.href,
      customerName: String(document.getElementById("qqName")?.value || "").trim(),
      customerEmail: String(document.getElementById("qqEmail")?.value || "").trim(),
      customerPhone: String(document.getElementById("qqPhone")?.value || "").trim(),
      stage: String(document.getElementById("qqStage")?.value || "").trim(),
      expectedInstallDate: String(document.getElementById("qqInstallDate")?.value || "").trim()
    }
  };
};

document.addEventListener("DOMContentLoaded", () => {
  const topSel = $("systemTypeTop");

  function initInstallDatePicker() {
  const installDateEl = $("qqInstallDate");
  if (!installDateEl || !window.flatpickr) return;

  if (installDateEl._flatpickr) {
    installDateEl._flatpickr.destroy();
  }

  flatpickr(installDateEl, {
    altInput: true,
    altFormat: "d-m-Y",
    dateFormat: "Y-m-d",
    allowInput: false,
    clickOpens: true,
    disableMobile: true,
    onReady: function(selectedDates, dateStr, instance) {
      if (instance.altInput) {
        instance.altInput.style.width = "100%";
        instance.altInput.style.maxWidth = "170px";
        instance.altInput.style.height = "48px";
        instance.altInput.style.padding = "12px";
        instance.altInput.style.border = "1px solid #cbd5e1";
        instance.altInput.style.borderRadius = "6px";
        instance.altInput.style.background = "#ffffff";
        instance.altInput.style.color = "#0f172a";
        instance.altInput.style.fontSize = "16px";
        instance.altInput.style.boxSizing = "border-box";
        instance.altInput.style.cursor = "pointer";
      }
    },
    onOpen: function(selectedDates, dateStr, instance) {
      if (instance.altInput) {
        instance.altInput.style.borderColor = "rgb(52, 172, 75)";
        instance.altInput.style.boxShadow = "0 0 0 3px rgba(52, 172, 75, 0.18)";
      }
    },
    onClose: function(selectedDates, dateStr, instance) {
      if (instance.altInput) {
        instance.altInput.style.borderColor = "#cbd5e1";
        instance.altInput.style.boxShadow = "none";
      }
    }
  });
}

  if (topSel) {
    topSel.addEventListener("change", () => {
      systemType = topSel.value || "mvhr";
      renderCalculator(systemType);
      initInstallDatePicker();
    });
  }

  systemType = topSel?.value || "mvhr";
  renderCalculator(systemType);
  initInstallDatePicker();
  bindPlanAiUpload();
  bindEstimatePopup();

  const phoneEl = $("qqPhone");
  const countryEl = $("qqPhoneCountry");

  phoneEl?.addEventListener("input", () => {
    const raw = String(phoneEl.value || "").replace(/\s+/g, "");

    if (raw.startsWith("+44") || raw.startsWith("0044") || raw.startsWith("44")) {
      countryEl.value = "UK";
    } else if (raw.startsWith("+353") || raw.startsWith("00353") || raw.startsWith("353")) {
      countryEl.value = "IE";
    }
  });
});



/* =========================================================
   STAGE-BASED DESIGN QUESTIONNAIRE OPTION
   - Planning / Foundations: no DQ option
   - Roof / 1st Fix: show DQ option and autofill form
========================================================= */

(function () {
  if (window.BPC_STAGE_DQ_ACCESS !== true) return;

  const DQ_STAGES = new Set(["roof", "first-fix", "firstfix", "1st fix", "roofing"]);

  function bpc$(id) {
    return document.getElementById(id);
  }

  function normaliseStage(value) {
    const raw = String(value || "").trim().toLowerCase();

    if (raw === "1st fix" || raw === "firstfix" || raw === "1st-fix") return "first-fix";
    if (raw === "roofing") return "roof";

    return raw;
  }

  function stageToZoho(value) {
    const stage = normaliseStage(value);

    if (stage === "planning") return "Planning";
    if (stage === "foundations") return "Foundations";
    if (stage === "roof") return "Roof";
    if (stage === "first-fix") return "1st Fix";

    return "-Select-";
  }

  function splitName(fullName) {
    const clean = String(fullName || "").trim().replace(/\s+/g, " ");
    if (!clean) return { first: "", last: "" };

    const parts = clean.split(" ");
    if (parts.length === 1) return { first: parts[0], last: "N/A" };

    return {
      first: parts.slice(0, -1).join(" "),
      last: parts.slice(-1).join(""),
    };
  }

  function dateToZoho(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const parts = raw.split("-");
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }

    return raw;
  }

  function setField(form, name, value) {
    if (!form) return;

    const el = form.querySelector(`[name="${CSS.escape(name)}"]`);
    if (!el) return;

    if (el.tagName === "SELECT") {
      const option = Array.from(el.options).find((opt) => opt.value === value);
      if (option) el.value = value;
      return;
    }

    el.value = value || "";
  }

  function checkService(form, value) {
    if (!form) return;

    form.querySelectorAll(`input[name="Checkbox"]`).forEach((box) => {
      if (box.value === value) box.checked = true;
    });
  }

  function getInstantSystemType() {
    return String(bpc$("systemTypeTop")?.value || "mvhr").toLowerCase();
  }

  function getPhoneCode() {
    const country = String(bpc$("qqPhoneCountry")?.value || "UK").toUpperCase();
    return country === "IE" ? "+353" : "+44";
  }

  function autofillDqForm() {
    const form = bpc$("bpcStageDqForm");
    if (!form) return;

    const nameRaw = String(bpc$("qqName")?.value || "").trim();
    const name = splitName(nameRaw);

    const stage = String(bpc$("qqStage")?.value || "").trim();
    const email = String(bpc$("qqEmail")?.value || "").trim();
    const phone = String(bpc$("qqPhone")?.value || "").trim();
    const installDate = String(bpc$("qqInstallDate")?.value || "").trim();

    setField(form, "Dropdown1", stageToZoho(stage));
    setField(form, "Date", dateToZoho(installDate));
    setField(form, "Name_First", name.first);
    setField(form, "Name_Last", name.last);
    setField(form, "Email", email);
    setField(form, "PhoneNumber_countrycodeval", getPhoneCode());
    setField(form, "PhoneNumber_countrycode", phone);

    if (!form.querySelector('[name="SingleLine1"]')?.value) {
      setField(form, "SingleLine1", nameRaw || "Instant Quote Project");
    }

    if (!form.querySelector('[name="Dropdown5"]')?.value || form.querySelector('[name="Dropdown5"]')?.value === "-Select-") {
      setField(form, "Dropdown5", "No");
    }

    if (!form.querySelector('[name="SingleLine"]')?.value) {
      setField(form, "SingleLine", "N/A");
    }

    if (!form.querySelector('[name="Dropdown4"]')?.value || form.querySelector('[name="Dropdown4"]')?.value === "-Select-") {
      setField(form, "Dropdown4", "N.Ireland");
    }

    if (!form.querySelector('[name="Dropdown"]')?.value || form.querySelector('[name="Dropdown"]')?.value === "-Select-") {
      setField(form, "Dropdown", "One off New build");
    }

    const system = getInstantSystemType();
    if (system === "mev") {
      checkService(form, "MEV (Mechanical Extract Ventilation)");
    } else {
      checkService(form, "Heat Recovery Ventilation (MVHR)");
    }

    const message = form.querySelector('[name="MultiLine"]');
    if (message && !message.value) {
      message.value =
        "Submitted from the Instant Quote page.\n" +
        `System selected: ${system.toUpperCase()}\n` +
        `Stage: ${stageToZoho(stage)}\n`;
    }
  }

  function updateDqVisibility() {
    const stage = normaliseStage(bpc$("qqStage")?.value);
    const option = bpc$("bpcStageDqOption");
    const panel = bpc$("bpcStageDqPanel");

    if (!option || !panel) return;

    const shouldShow = DQ_STAGES.has(stage);

    option.hidden = !shouldShow;

    if (!shouldShow) {
      panel.hidden = true;
      return;
    }

    autofillDqForm();
  }

  function openDqPanel() {
    const panel = bpc$("bpcStageDqPanel");
    if (!panel) return;

    autofillDqForm();
    panel.hidden = false;

    panel.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  function closeDqPanel() {
    const panel = bpc$("bpcStageDqPanel");
    if (!panel) return;

    panel.hidden = true;
  }

  function continueInstantQuote() {
    const btn = bpc$("airflowCalcBtn");
    if (!btn) return;

    btn.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setTimeout(() => btn.click(), 200);
  }

  function bindStageDq() {
    const stage = bpc$("qqStage");
    const openBtn = bpc$("bpcOpenStageDqBtn");
    const closeBtn = bpc$("bpcCloseStageDqBtn");
    const instantBtn = bpc$("bpcContinueInstantOnlyBtn");

    stage?.addEventListener("change", updateDqVisibility);

    ["qqName", "qqEmail", "qqPhone", "qqPhoneCountry", "qqInstallDate", "systemTypeTop"].forEach((id) => {
      const el = bpc$(id);
      if (!el) return;

      el.addEventListener("input", autofillDqForm);
      el.addEventListener("change", autofillDqForm);
    });

    openBtn?.addEventListener("click", openDqPanel);
    closeBtn?.addEventListener("click", closeDqPanel);
    instantBtn?.addEventListener("click", continueInstantQuote);

    updateDqVisibility();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bindStageDq);
  } else {
    bindStageDq();
  }
})();